import classnames from 'classnames';
import React from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { TransferUploadActions } from '../../../transfer-bundle/store/actions';
import VideoIllustration from '@wetransfer/transfer/component/illustration/video-illustration';
import Helper from '@wetransfer/transfer/helper';
import I18n from '@wetransfer/transfer/modules/i18n';
import Router from '@wetransfer/transfer/modules/router';
import Actions from '@wetransfer/transfer/store/action';
import { State, SUBSCRIPTION_STATUS } from '@wetransfer/transfer/store/types';
import { getIsForwardedTransfer } from '@wetransfer/transfer/transfer-bundle/store/selectors';

const mapStateToProps = (state: State) => ({
  isForwardedTransfer: getIsForwardedTransfer(state),
  planTier: state.user.planTier,
});

const mapDispatchToProps = {
  resetTransfer: (TransferUploadActions as { resetTransfer: () => void })
    .resetTransfer,
  signout: Actions.User.signout.bind(Actions.User),
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props {
  transfer: {
    lastError: string;
  };
  storageLimit: number;
}

function gotoTransfers() {
  Router.navigateTo('/transfers');
}

function gotoHelp() {
  Router.navigateTo('/help');
}

function gotoPayment() {
  Router.navigateTo('/payment/retry');
}

export function getInactiveSubscriptionStatus(
  subscription: State['account']['subscription']
): 'AccountExpired' | 'AccountInactive' {
  return subscription.status === SUBSCRIPTION_STATUS.canceled
    ? 'AccountExpired'
    : 'AccountInactive';
}

export function getError(error: string): {
  button: string;
  title?: string;
  text: string;
  supportingText?: string;
  supportingLink?: string;
  informationText?: string;
  informationLink?: string;
  action?: () => void;
} {
  switch (error) {
    case 'LimitReached':
      return {
        button: I18n.t('transfer.storage_full_block.cta'),
        action: gotoTransfers,
        title: I18n.t('transfer.storage_full_block.title'),
        text: I18n.t('transfer.storage_full_block.information_detail_text'),
        informationLink: I18n.t(
          'transfer.storage_full_block.information_detail_link'
        ),
        supportingText: I18n.t(
          'transfer.storage_full_block.instruction_detail_text'
        ),
        supportingLink: I18n.t(
          'transfer.storage_full_block.instruction_detail_link'
        ),
      };
    case 'UserBlocked':
      return {
        text: I18n.t('transfer.bubble.errors.blocked'),
        button: I18n.t('transfer.buttons.help'),
        action: gotoHelp,
      };
    case 'AccountExpired':
      return {
        title: I18n.t('transfer.account_expired.title'),
        text: I18n.t('transfer.account_expired.text'),
        button: I18n.t('transfer.buttons.payment'),
        action: gotoPayment,
      };
    case 'AccountInactive':
      return {
        title: I18n.t('transfer.account_inactive.tiers.title'),
        text: 'transfer.account_inactive.tiers.text',
        button: I18n.t('form.account.billing_cta'),
        action: gotoPayment,
      };

    default:
      return {
        button: I18n.t('transfer.buttons.retry'),
        title: I18n.t('transfer.failed.title'),
        text: 'transfer.failed.text',
      };
  }
}

export class Aborted extends React.Component<PropsFromRedux & Props> {
  node?: HTMLDivElement;

  doSignout = (event: Event): void => {
    const { signout, isForwardedTransfer } = this.props;
    event.preventDefault();
    signout({ isForwardedTransfer });
  };

  componentDidMount(): void {
    if (
      this.props.transfer.lastError === 'AccountExpired' ||
      this.props.transfer.lastError === 'AccountInactive'
    ) {
      if (!this.node) {
        return;
      }

      const signoutLink = this.node.querySelector<HTMLAnchorElement>('p a');

      if (!signoutLink) {
        return;
      }

      signoutLink['onclick'] = this.doSignout;
    }
  }

  // Let's render our aborted screen (spinner?) and pause buttons.
  render(): JSX.Element {
    const {
      button,
      action,
      title,
      text,
      supportingText,
      supportingLink,
      informationLink,
    } = getError(this.props.transfer.lastError);
    const { storageLimit, planTier } = this.props;

    const storageLimitReached =
      this.props.transfer.lastError === 'LimitReached';

    return (
      <div
        ref={(node) => (this.node = node)}
        className="transfer__window uploader uploader--error"
      >
        <div className="transfer__contents">
          <VideoIllustration
            className={storageLimitReached && 'storage-used'}
            type="transfer-aborted"
            src="transfer_window/transfer_error"
          />
          <h2
            className={classnames(storageLimitReached && 'storage-used__title')}
          >
            {title}
          </h2>

          {informationLink ? (
            <p>
              <span>{text}</span>
              <a
                target="_blank"
                href="https://help.wetransfer.com/hc/en-us/articles/4469847247508-Your-WeTransfer-Premium-unlimited-storage"
                rel="noreferrer"
              >
                {informationLink}
              </a>
            </p>
          ) : (
            <p
              dangerouslySetInnerHTML={{
                __html: I18n.t(text, {
                  plan: Helper.getPlanName(planTier),
                  storageLimit: I18n.size(storageLimit, {
                    precision: 0,
                  }),
                }),
              }}
            />
          )}

          {supportingText && (
            <p className="supporting-text">
              <span
                dangerouslySetInnerHTML={{
                  __html: supportingText,
                }}
              />
              {supportingLink && (
                <a
                  target="_blank"
                  href="https://we.tl/increasestorage"
                  rel="noreferrer"
                >
                  {supportingLink}
                </a>
              )}
            </p>
          )}
        </div>
        <div className="transfer__footer">
          <button
            className="transfer__button transfer__button--alt"
            onClick={action || this.props.resetTransfer}
          >
            {button}
          </button>
        </div>
      </div>
    );
  }
}

export default connector(Aborted);
